/**
 * Plain words for a failed Access Labs request, shared by the Access Admin
 * page and the Access order screens.
 *
 * AccessOrdersController answers a refusal with a reason code (422, 404 for a
 * row that is not the practice's, 403 MODULE_DISABLED, 502 ORDER_NOT_SENT or
 * ORDER_NOT_CONFIRMED).
 * Each screen keeps a map from those codes to text that says what is wrong and
 * what to do; this file holds the lookup and the order screens' map.
 */
import { getErrorCode, getErrorMessage } from '@/services/apiClient';

export const ACCESS_MODULE_DISABLED_MESSAGE =
  "Access Labs isn't switched on for your practice. Ask your administrator to switch on Access Labs, then try again.";

/**
 * The message to show for a failed Access request. A known refusal code maps
 * to its plain message; a code in `named` prefers the server's message, which
 * names the item (the provider, the test), and falls back to the mapped text;
 * an unknown code shows the server's (already plain) message; anything with no
 * code (a dropped connection, a script error) shows `fallback`, never its raw
 * text.
 */
export function accessErrorMessage(
  err: unknown,
  fallback: string,
  refusals: Record<string, string>,
  named: ReadonlySet<string> = new Set(),
): string {
  const code = getErrorCode(err);
  if (code === null) return fallback;
  if (named.has(code)) return getErrorMessage(err, refusals[code] ?? fallback);
  return refusals[code] ?? getErrorMessage(err, fallback);
}

/**
 * The order and resend refusals (AccessOrderService). Keep in step with the
 * server's wording, which is used first for the named codes below.
 */
export const ACCESS_ORDER_REFUSALS: Record<string, string> = {
  MODULE_DISABLED: ACCESS_MODULE_DISABLED_MESSAGE,
  LOCATION_REQUIRED: 'Choose the location this order is being placed from, then send the order again.',
  PATIENT_REQUIRED: "This order isn't linked to a patient. Close the order form, open it again from the patient's Labs tab, then send the order again.",
  TESTS_REQUIRED: 'Add at least one test, then send the order again.',
  TEST_NOT_ORDERABLE: 'One of these tests can no longer be ordered at this location. Remove it from the order and try again.',
  PROVIDER_REQUIRED: 'Choose the ordering provider, then send the order again.',
  PROVIDER_NOT_AT_LOCATION: "This provider isn't set up to order Access Labs tests at this location. Ask your practice administrator to set them up for this location on the Access Labs admin page, under Providers, then send the order again.",
  LOCATION_NOT_SET_UP: 'Access Labs is not set up for this location yet. Ask your system administrator to finish setting up Access Labs.',
  PATIENT_NOT_FOUND: "This patient wasn't found in your practice. Close the order form, open the patient's chart again, then place the order from their Labs tab.",
  ORDER_NOT_SENT: "The order was saved but has not been sent to Access Labs. You can resend it from the patient's Labs tab without entering it again.",
  ORDER_NOT_CONFIRMED: "The order was saved. Access Labs may have received it, but didn't confirm it. Check with Access Labs before sending it again. It is on the patient's Labs tab.",
  ORDER_MAY_HAVE_ARRIVED: "Access Labs may already have this order. Check with Access Labs, and send it again only if they say they didn't receive it.",
  ORDER_ALREADY_SENT: "This order has already been sent to Access Labs, so it can't be sent again. Refresh the page to see its latest status.",
  ORDER_SENDING: 'This order is being sent right now. Wait a moment, then refresh the page to see whether it went.',
  ORDER_CANNOT_BE_RESENT: "This order can't be resent because it wasn't saved in full. Place the order again from the patient's Labs tab.",
  ORDER_NOT_FOUND: "That order wasn't found. Refresh the page and try again.",
};

/** Order refusals whose server message names the provider, the location, the tests or the reason. */
export const ACCESS_ORDER_NAMED_REFUSALS: ReadonlySet<string> = new Set([
  'PROVIDER_NOT_AT_LOCATION',
  'TEST_NOT_ORDERABLE',
  'ORDER_NOT_SENT',
  'ORDER_NOT_CONFIRMED',
]);

/** The plain message for a failed Access order or resend. */
export function accessOrderErrorMessage(err: unknown, fallback: string): string {
  return accessErrorMessage(err, fallback, ACCESS_ORDER_REFUSALS, ACCESS_ORDER_NAMED_REFUSALS);
}
